import React, { useRef, useLayoutEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import PriceWithCurrency from "@/components/Helpers/PriceWithCurrency";
import ImgTag from "@/components/Helpers/ImgTag";
import { roundPrice } from "@/utils/global/handlePrice";
import EWSelectorHeader from "./EWSelectorHeader";

const CheckIcon = ({ className }) => (
  <svg
    width="10"
    height="8"
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M1 4L3.5 6.5L9 1"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TooltipCaret = () => (
  <svg
    width="20"
    height="10"
    viewBox="0 0 20 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="text-[#00269A] campaign:text-campaignAccent"
  >
    <path d="M0 0L10 10L20 0H0Z" fill="currentColor" />
  </svg>
);

const DurationCard = ({ duration, isSelected, onClick, t, compact, locale }) => {
  const handleClick = () => onClick(duration.price_id);

  return (
    <div
      onClick={handleClick}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
      className={[
        "relative cursor-pointer flex flex-col items-center justify-center",
        "rounded-[8px] p-[8px] transition-all duration-200 border",
        compact ? "h-[72px]" : "h-[79px]",
        "bg-[rgba(255,255,255,0.60)]",
        isSelected
          ? "border-[#00269A] campaign:border-campaignAccent"
          : "border-[rgba(113,113,122,0.15)] hover:border-[#00269A] campaign:hover:border-campaignAccent",
      ].join(" ")}
    >
      {/* start-[6px] is logical → RTL-safe */}
      <div className="absolute top-[6px] start-[6px]">
        <div
          className={[
            "w-[20px] h-[20px] rounded-[5px] flex items-center justify-center border transition-colors",
            isSelected
              ? "bg-[#00269A] border-[#00269A] campaign:bg-campaignAccent campaign:border-campaignAccent"
              : "bg-white border-[#E6E6E6]",
          ].join(" ")}
          style={
            isSelected
              ? undefined
              : { boxShadow: "0px 0.89px 5.838px 0px rgba(0,0,0,0.10)" }
          }
        >
          {isSelected && <CheckIcon className="w-3 h-3" />}
        </div>
      </div>

      <div
        className={[
          "flex items-center justify-center w-full font-[500] leading-[40px]",
          locale === "ar" ? "font-noto" : "font-poppins",
          "-mb-1",
          compact ? "text-[11px]" : "text-[13px] md:text-[16px]",
          isSelected ? "text-[#00269A] campaign:text-campaignAccent" : "text-[#6B7280]",
        ].join(" ")}
        style={{ minHeight: "40px" }}
      >
        {duration.duration ||
          `${duration.year} ${duration.year > 1 ? t("years") : t("year")}`}
      </div>

      <div
        className={[
          "flex items-center gap-[3px]",
          locale === "ar" ? "font-noto" : "font-poppins",
          compact ? "text-[11px]" : "text-[13px] md:text-[16px]",
          isSelected
            ? "text-[#00269A] campaign:text-campaignAccent font-[700]"
            : "text-[#6B7280] font-[600]",
        ].join(" ")}
      >
        <PriceWithCurrency
          amount={roundPrice(duration.price)}
          iconClassName="w-[11.634px] h-[13.437px]"
          iconStyle={{ width: "11.634px", height: "13.437px" }}
          className="gap-[3px]"
        />
      </div>
    </div>
  );
};

/**
 * 2-level warranty selector — category tabs, then the selected category's
 * duration cards, linked by a caret. Shared by the PDP and cart/mini-cart.
 *
 * onCategoryChange must also reset selectedPriceId to null.
 * configuration — BE-localised header block (see getEwHeaderConfig).
 */
const EWPdpSelector = ({
  categories,
  selectedCategoryId,
  selectedPriceId,
  onCategoryChange,
  onDurationChange,
  onDurationClear,
  onMoreInfo,
  locale,
  isPending = false,
  compact = false,
  configuration,
}) => {
  const { t } = useTranslation("extended_warranty");
  const isRTL = locale === "ar";

  // Which category is "open" in the 2-level view. Priority:
  //  1. a tab the user explicitly tapped (selectedCategoryId)
  //  2. the category that owns the currently-selected duration
  //  3. the first category, so the plans are visible on load — the group label
  //     is auto-active but no duration is preselected
  const catList = Array.isArray(categories) ? categories : [];
  const effectiveCategoryId =
    selectedCategoryId ??
    catList.find((c) =>
      (c.durations || []).some((d) => d.price_id === selectedPriceId),
    )?.category_id ??
    catList[0]?.category_id ??
    null;

  const tabRowRef = useRef(null);
  const [caretOffset, setCaretOffset] = useState(null); // px from the row's physical-left edge

  useLayoutEffect(() => {
    if (!effectiveCategoryId || !tabRowRef.current) {
      setCaretOffset(null);
      return;
    }
    const row = tabRowRef.current;
    const activeBtn = row.querySelector(
      "[data-cat-id='" + effectiveCategoryId + "']",
    );
    if (!activeBtn) {
      setCaretOffset(null);
      return;
    }
    const rowRect = row.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    // getBoundingClientRect().left is physical in both dirs, so always use `left:`
    // — positioning with `right:` for RTL mirrors it (tab 1 → tab 3).
    const centerInRow = btnRect.left - rowRect.left + btnRect.width / 2;
    setCaretOffset(centerInRow - 10); // -10 = half the 20px caret → centred
  }, [effectiveCategoryId, categories]);

  if (!Array.isArray(categories) || categories.length === 0) return null;

  const activeCategory = categories.find(
    (c) => c.category_id === effectiveCategoryId,
  );

  const handleDurationClick = (priceId) => {
    if (priceId === selectedPriceId) {
      if (onDurationClear) onDurationClear();
    } else {
      if (onDurationChange) onDurationChange(priceId);
    }
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={[
        "w-full max-w-4xl mx-auto my-2 md:my-[16px]",
        "rounded-[10px]",
        "bg-[rgba(137,175,255,0.10)]",
        "p-3 md:px-[20px] md:pb-[20px] md:pt-[13px]",
        "flex flex-col gap-[20px]",
      ].join(" ")}
    >
      <EWSelectorHeader
        configuration={configuration}
        isRTL={isRTL}
        onMoreInfo={onMoreInfo}
        compact={compact}
      />

      <div
        ref={tabRowRef}
        className={
          compact
            ? "grid grid-cols-3 items-stretch gap-2 w-full"
            : "grid grid-cols-3 items-stretch gap-2 md:gap-[16px] w-full"
        }
      >
        {categories.map((cat) => (
          <button
            key={cat.category_id}
            data-cat-id={cat.category_id}
            type="button"
            onClick={() =>
              onCategoryChange && onCategoryChange(cat.category_id)
            }
            aria-pressed={cat.category_id === effectiveCategoryId}
            className={[
              "flex items-center justify-center rounded-[8px] p-[8px] transition-all duration-200",
              compact
                ? "min-h-[44px] py-1"
                : "min-h-[56px]",
              // leading-[1.2] (not tied to tab height) keeps multi-word labels
              // snug when they wrap to 2 lines in the narrow 1/3-width tabs.
              compact
                ? "text-[11px] font-[500] leading-[1.2] text-center"
                : "text-[13px] md:text-[16px] font-[500] leading-[1.2] text-center",
              isRTL ? "font-noto" : "font-poppins",
              cat.category_id === effectiveCategoryId
                ? "bg-[#00269A] border-[#00269A] text-white campaign:bg-campaignAccent campaign:border-campaignAccent"
                : "bg-white border-[rgba(113,113,122,0.15)] text-[#6B7280] hover:border-[#00269A] campaign:hover:border-campaignAccent",
            ].join(" ")}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* -mt-[20px] cancels the parent gap so the caret meets the tab; top:-1
          overlaps the tab edge by 1px so the two blues merge with no seam. */}
      {activeCategory && (
        <div className="flex flex-col -mt-[20px]">
          <div className="relative h-[10px] w-full" aria-hidden="true">
            <span
              className="absolute"
              style={
                caretOffset !== null
                  ? { left: caretOffset, top: -1 }
                  : { left: "50%", transform: "translateX(-50%)", top: -1 }
              }
            >
              <TooltipCaret />
            </span>
          </div>

          <div
            className={
              compact
                ? "grid grid-cols-3 items-center gap-2 w-full mt-[8px]"
                : "grid grid-cols-3 items-center gap-2 md:gap-[16px] w-full mt-[8px]"
            }
          >
            {activeCategory.durations.map((dur) => (
              <DurationCard
                key={dur.price_id}
                duration={dur}
                isSelected={dur.price_id === selectedPriceId}
                onClick={handleDurationClick}
                t={t}
                compact={compact}
                locale={locale}
              />
            ))}
          </div>
        </div>
      )}

      {isPending && (
        <div className="text-[12px] text-[#6B7280] px-1" aria-live="polite">
          {t("updating", { defaultValue: "Updating..." })}
        </div>
      )}
    </div>
  );
};

export default EWPdpSelector;
